import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, orderBy, addDoc, getDocs, where, serverTimestamp, increment, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { sendNotification } from '../../utils/notificationService';
import { UserPlus, Search, Check, Loader2, AlertCircle, Trash2 } from 'lucide-react';

export default function AdminRegistrations() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'course_registrations'), orderBy('requestedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleApprove = async (req) => {
    if (!window.confirm('موافقة على طلب التسجيل؟')) return;
    setActionLoading(req.id);
    try {
      // 1. Update course_registrations status
      await updateDoc(doc(db, 'course_registrations', req.id), { status: 'approved' });

      // 2. Create enrollment (activates the course for the student)
      const enrollQ = query(collection(db, 'enrollments'), where('userId', '==', req.studentId), where('courseId', '==', req.courseId));
      const enrollSnap = await getDocs(enrollQ);
      if (enrollSnap.empty) {
        await addDoc(collection(db, 'enrollments'), {
          userId: req.studentId,
          courseId: req.courseId,
          courseTitle: req.courseName,
          enrolledAt: serverTimestamp(),
        });
        // 3. Increment course student count
        await updateDoc(doc(db, 'courses', req.courseId), { studentsCount: increment(1) });
      }

      // 4. Add to registrations (students list) as 'customer'
      const regQ = query(collection(db, 'registrations'), where('userId', '==', req.studentId), where('courseId', '==', req.courseId));
      const regSnap = await getDocs(regQ);
      if (regSnap.empty) {
        // Fetch student contact info from their profile
        let studentWhatsapp = req.studentWhatsapp || '';
        let studentEmail = '';
        let studentLevel = req.studentLevel || 'beginner';
        try {
          const userDoc = await getDoc(doc(db, 'users', req.studentId));
          if (userDoc.exists()) {
            if (!studentWhatsapp) studentWhatsapp = userDoc.data().phone || userDoc.data().whatsapp || '';
            if (studentLevel === 'beginner' && userDoc.data().designLevel) studentLevel = userDoc.data().designLevel;
            studentEmail = userDoc.data().email || '';
          }
        } catch (err) {
          console.error('Error fetching student profile:', err);
        }

        await addDoc(collection(db, 'registrations'), {
          name: req.studentName,
          userId: req.studentId,
          courseId: req.courseId,
          courseTitle: req.courseName,
          whatsapp: studentWhatsapp,
          level: studentLevel,
          email: studentEmail,
          status: 'customer',
          createdAt: serverTimestamp(),
        });
      }

      // 5. Notify student
      await sendNotification(req.studentId, 'تم قبول طلبك', `تم الموافقة على طلب تسجيلك في دورة: ${req.courseName}`, 'success', `/dashboard/courses`);

      // 6. Notify instructor
      try {
        const courseDoc = await getDoc(doc(db, 'courses', req.courseId));
        if (courseDoc.exists() && courseDoc.data().instructorId) {
          await sendNotification(
            courseDoc.data().instructorId,
            'طالب جديد في دورتك',
            `انضم الطالب ${req.studentName} إلى دورتك "${req.courseName}".`,
            'info',
            '/instructor-dashboard/students'
          );
        }
      } catch (err) {
        console.error('Error notifying instructor:', err);
      }
    } catch (e) {
      console.error('Approval error:', e);
      alert('خطأ في الموافقة!');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (req) => {
    if (!window.confirm('رفض وحذف الطلب؟')) return;
    setActionLoading(req.id);
    try {
      await deleteDoc(doc(db, 'course_registrations', req.id));
      await sendNotification(req.studentId, 'تم رفض طلبك', `نعتذر، لم يتم قبول طلبك للتسجيل في دورة: ${req.courseName}`, 'warning', '/dashboard');
    } catch (e) {
      alert('خطأ في الحذف!');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredRequests = requests.filter(r => 
    r.status === 'pending' && 
    (r.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) || r.courseName?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">طلبات التسجيل</h1>
          <p className="text-slate-500 dark:text-zinc-400">إدارة طلبات التحاق الطلاب بالدورات.</p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl shadow-xl mb-6 p-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          <input type="text" placeholder="ابحث باسم الطالب أو الدورة..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-11 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white transition-all font-bold" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary-600" /></div>
      ) : filteredRequests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-slate-500 bg-white/50 dark:bg-zinc-900/40 backdrop-blur-3xl rounded-3xl border border-slate-200/50 dark:border-white/10">
          <AlertCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
          <p className="text-xl font-bold">لا توجد طلبات معلقة</p>
        </div>
      ) : (
        <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-800/50 border-b border-slate-200/50 dark:border-white/10 text-slate-500 dark:text-zinc-400">
                  <th className="px-6 py-4 font-bold">الطالب</th>
                  <th className="px-6 py-4 font-bold">رقم الواتساب</th>
                  <th className="px-6 py-4 font-bold">المستوى</th>
                  <th className="px-6 py-4 font-bold">الدورة</th>
                  <th className="px-6 py-4 font-bold">تاريخ الطلب</th>
                  <th className="px-6 py-4 font-bold w-48">الدفع</th>
                  <th className="px-6 py-4 font-bold w-32 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredRequests.map(req => (
                  <tr key={req.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                          <UserPlus className="w-5 h-5"/>
                        </div>
                        {req.studentName || 'طالب مجهول'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-zinc-300 font-mono tracking-widest" dir="ltr">
                      {req.studentWhatsapp || '-'}
                    </td>
                    <td className="px-6 py-4 font-bold">
                      {req.studentLevel === 'expert' ? 'خبير' : req.studentLevel === 'intermediate' ? 'متوسط' : 'مبتدئ'}
                    </td>
                    <td className="px-6 py-4 font-bold text-primary-600 dark:text-primary-400">{req.courseName}</td>
                    <td className="px-6 py-4 text-slate-500 dark:text-zinc-400 font-medium" dir="ltr">
                      {req.requestedAt?.toDate ? req.requestedAt.toDate().toLocaleDateString('en-GB') : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {req.receiptUrl ? (
                        <div className="flex flex-col gap-1 text-sm">
                          <span className="font-bold text-slate-700 dark:text-zinc-300">
                            {req.paymentMethod}
                          </span>
                          <span className="text-xs text-slate-500" dir="ltr">{req.referenceNumber}</span>
                          <a href={req.receiptUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-primary-600 hover:text-primary-500 font-bold text-xs">
                            <span className="underline">عرض السند</span>
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">بدون إرفاق</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleApprove(req)} disabled={actionLoading === req.id}
                          className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 flex items-center justify-center hover:bg-emerald-500 hover:text-white dark:hover:bg-emerald-500 transition-all">
                          {actionLoading === req.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        </button>
                        <button onClick={() => handleReject(req)} disabled={actionLoading === req.id}
                          className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 flex items-center justify-center hover:bg-rose-500 hover:text-white dark:hover:bg-rose-500 transition-all">
                          {actionLoading === req.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
